import AppResource from '@milestoneslabs/milestones-js-engine/src/Data/AppResource';
import OrganizationAppResource from './OrganizationAppResource';
import MLSAppResource from './MLSAppResource';
import PlaybookAppResource from './PlaybookAppResource';
import FileAppResource from './FileAppResource';
import Engine from '@milestoneslabs/milestones-js-engine';
import isEmpty from 'lodash/isEmpty';
import LuHubImportSourceAppResource
    from '@milestoneslabs/milestones-core-js/src/AppResources/LuHubImportSourceAppResource';
import UserAppResource from './UserAppResource';
import LuCommunicationContextAppResource from "./LuCommunicationContextAppResource";
import LuHubImportStatusAppResource
    from '@milestoneslabs/milestones-core-js/src/AppResources/LuHubImportStatusAppResource';

export const SINGLE = 'single';
export const SUMMARY = 'summary';
export const OPT_IN = 'opt-in';
export const OPT_OUT = 'opt-out';

class OrganizationHubImportAppResource extends AppResource {

    /**
     * @returns {string}
     */
    appResourceType() {
        return 'organization-hub-import';
    }

    /**
     * @returns {string}
     */
    appResourceApiPrefix() {
        return 'organizations/hub/import';
    }

    /**
     * @returns {*[]}
     */
    hidden() {
        return super.hidden().concat([
            'uuid',
        ]);
    }

    apiPaths() {
        return {
            ...super.apiPaths(),
            'generateReport': Engine.apiUrl('organizations/hub/import/{uuid}/generate-report'),
            'import': Engine.apiUrl('organizations/hub/import/{uuid}/import'),
        };
    }

    async generateReportFile() {
        if (!this.uuid) {
            return Promise.resolve(null);
        }

        return await this.enginePost(this.apiPaths().generateReport.replace('{uuid}', this.uuid));
    }

    async import() {
        if (!this.uuid) {
            return Promise.resolve(null);
        }

        const apiResponse = await Engine.get(this.apiPaths().import.replace('{uuid}', this.uuid));

        if (isEmpty(apiResponse.resources) ||
            !apiResponse.resources.has(this.uuid)) {
            return Promise.resolve(null)
        }

        this.syncFromRemoteAppResource(apiResponse.resources.get(this.uuid))

        return apiResponse;
    }

    /**
     * @returns {*[]}
     */
    fillable() {
        return [
            'organizationUuid',
            'mlsDefinitionUuid',
            'playbookUuid',
            'originalFileUuid',
            'validationReportFileUuid',
            'hubsReportFileUuid',
            'validationStatus',
            'imported',
            'disabled',
            'validationSuccessPercent',
            'requestedHubsReportFile',
            'file',
            'lookupHubImportSourceUuid',
            'userUuid',
            'proCommunications',
            'invitationType',
            'lookupCommunicationContextUuid',
        ];
    }

    /**
     * @returns {{mls: MLSAppResource, originalFile: FileAppResource, validationReportFile: FileAppResource, organization: OrganizationAppResource, hubsReportFile: FileAppResource, playbook: PlaybookAppResource}}
     */
    relationMap() {
        return {
            organization: OrganizationAppResource,
            mls: MLSAppResource,
            playbook: PlaybookAppResource,
            originalFile: FileAppResource,
            validationReportFile: FileAppResource,
            hubsReportFile: FileAppResource,
            hubImportSource: LuHubImportSourceAppResource,
            hubImportStatus: LuHubImportStatusAppResource,
            uploader: UserAppResource,
            lookupCommunicationContext: LuCommunicationContextAppResource,
        };
    }

    /**
     * @returns {*[]}
     */
    relationForeignKeys() {
        return {
            organization: 'organization_uuid',
            mls: 'mls_definition_uuid',
            playbook: 'playbook_uuid',
            originalFile: 'original_file_uuid',
            validationReportFile: 'validation_report_file_uuid',
            hubsReportFile: 'hubs_report_file_uuid',
            hubImportSource: 'lookup_hub_import_source_uuid',
            uploader: 'user_uuid',
            lookupCommunicationContext: 'lookup_communication_context_uuid',
            hubImportStatus: 'lookup_hub_import_status_uuid',
        };
    }

    /**
     * @returns {*}
     */
    validationRules() {
        return {
            organizationUuid: {
                type: 'string',
                presence: true,
            },
            mlsDefinitionUuid: {
                type: 'string',
            },
            playbookUuid: {
                type: 'string',
                presence: true,
            },
            originalFileUuid: {
                type: 'string',
            },
            validationReportFileUuid: {
                type: 'string',
            },
            hubsReportFileUuid: {
                type: 'string',
            },
            validationStatus: {
                type: 'string',
            },
            imported: {
                type: 'boolean',
            },
            deleted: {
                type: 'boolean',
            },
            validationSuccessPercent: {
                numericality: true,
            },
            requestedHubsReportFile: {
                type: 'boolean',
            },
            file: {
                type: 'object',
            },
            lookupHubImportSourceUuid: {
                type: 'string',
            },
            user_uuid: {
                type: 'string',
            },
            proCommunications: {
                type: 'string',
            },
            invitationType: {
                type: 'string',
            },
            lookupCommunicationContextUuid: {
                type: 'string',
            },
        };
    }

    async enginePost(url, data, config = {}) {
        let additionalHeaders = {};

        if (data.originalFile && !(data instanceof FormData)) {
            data = objectToFormData(data);
            additionalHeaders['Content-Type'] = 'multipart/form-data';
        }

        try {
            const r = super.enginePost(url, data, {
                ...config,
                headers: {
                    ...config.headers,
                    ...additionalHeaders,
                },
            });
            return r;
        } catch (e) {
            throw e;
        }
    }
}

export default OrganizationHubImportAppResource;
